
import IconButton from '@white-label-icon/icon-button';
import { defineComponent, PropType } from 'vue';
import type {
  CustomersValues,
  CustomersOptions,
  CustomerItem,
  CustomerSelectedAgeSelectListItems,
  CustomerChildAgeSelectorSelectedValue,
} from '@white-label-types/customers';

import { NumSelector, DropdownWrapper } from 'ui-shared-components';
import { isDedicatedFlow } from '@white-label-helper/dedicated-flow';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { arrowKeyNavigationMixin } from '@white-label-helper/shared-helpers';

const defaultCustomerValues: CustomersValues = {
  adult: 1,
  child: 0,
  senior: 0,
  infant: 0,
};

export default defineComponent({
  name: 'CustomersSelector',
  components: {
    IconButton,
    NumSelector,
    DropdownWrapper,
  },

  mixins: [arrowKeyNavigationMixin],

  props: {
    config: {
      type: Object as PropType<CustomersOptions>,
      default(): CustomersOptions {
        return {
          max_tickets: 9,
          adult: {
            min_age: 16,
            max_age: 55,
          },
          senior: {
            min_age: 55,
          },
          child: {
            min_age: 3,
            max_age: 30,
          },
          infant: {
            max_age: 3,
          },
        };
      },
    },
    minAdults: {
      type: Number,
      default: 0,
    },
    value: {
      type: Object as PropType<CustomersValues>,
      default() {
        return {};
      },
    },
    showAgeRanges: {
      type: Boolean,
      default: false,
    },
    availability: {
      type: Object,
      default() {
        return {
          adult: {
            isAvailable: true
          },
          senior: {
            isAvailable: true
          },
          child: {
            isAvailable: true
          },
          infant: {
            isAvailable: true
          },
        };
      },
    },
    closeSelectorTriggered: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      values: {
        ...defaultCustomerValues,
      } as CustomersValues,
      customerChildAgeSelectorsList: [] as CustomerSelectedAgeSelectListItems[],
      customerChildAgeRangesList: [] as CustomerChildAgeSelectorSelectedValue[],
      isDedicatedFlow: isDedicatedFlow(this.$route),
    };
  },
  computed: {
    defaultList(): CustomerItem[] {
      return [
        {
          id: 'senior',
          title: this.$tc('shared.customers.senior', 1),
          isAdult: true,
        },
        {
          id: 'adult',
          title: this.$tc('shared.customers.adult', 1),
          isAdult: true,
        },
        {
          id: 'child',
          title: this.$tc('shared.customers.child', 1),
        },
        {
          id: 'infant',
          title: this.$tc('shared.customers.infant', 1),
        },
      ] as CustomerItem[];
    },
    getChildListCount() {
      // Retrieve the selected value indicating the number of children from the list
      const childSelectorValue = this.list.filter(item => item.id === 'child')[0]?.value;

      // Ensure the retrieved value is a valid number and greater than zero
      if (typeof childSelectorValue === 'number' && childSelectorValue > 0) {
        // Construct an array of dropdown configuration objects
        const childAgeDropDownArray = [...Array(childSelectorValue).keys()].map(index => ({
          isDropDownOpened: false,
          id: 'childAgeDropDown-' + (index + 1),
          selectedValue: {id: '' , name: ''},
          error: false
        }));

        return childAgeDropDownArray;
      }

      return [];
    },
    list(): CustomerItem[] {
      const { defaultList, values, config } = this;
      const MAX_DEDICATED_FLOW_TICKETS_PER_TYPE = 60;
      const list = this.filterListBasedOnConfig();
      const { length } = list;
      let adultCount = 0;
      let childCount = 0;
      defaultList.forEach(({ isAdult, id }) => {
        if (isAdult) {
          adultCount += values[id];
        }
        if (!isAdult) {
          childCount += values[id];
        }
      });
      const totalCount = adultCount + childCount;
      return list.map((item, index) => {
        const ageRange = config[item.id];
        const min_age = ageRange?.min_age ?? 0;
        const max_age = ageRange?.max_age ?? '';
        const { isAdult = false } = item;
        let minus_disabled = false;
        let plus_disabled = false;
        const value = values[item.id];
        let label = `${min_age} - ${max_age}`;
        if (!max_age) {
          label = `${min_age}+`;
        }
        if (index === length - 1 && max_age) {
          label += ` ${this.$tc('shared.customers.yearOld', max_age)}`;
        }

        if (!isAdult && adultCount === 0) {
          plus_disabled = true;
        }
        /**
         * https://magusa.atlassian.net/browse/CUS-2508
         * Add support for increased guest count in dedicated flow
         */
        if (
          (!this.isDedicatedFlow && totalCount >= config.max_tickets) ||
          (getFeatureFlag('CUS_2533_DEDICATED_BOOKING_FLOW') && value >= MAX_DEDICATED_FLOW_TICKETS_PER_TYPE)
        ) {
          plus_disabled = true;
        }
        if (value === 0 || (isAdult && adultCount === this.minAdults)) {
          minus_disabled = true;
        }
        const isAvailable = this.availability[item.id].isAvailable;

        return {
          ...item,
          label: this.isChildAgeConfigurationAvailable && !isAdult ? '' : label,
          minus_disabled,
          plus_disabled,
          value,
          isAvailable
        };
      });
    },
    isChildAgeConfigurationAvailable(){
      const { config } = this;
      return config.child_age_configuration;
    },
    hasAdult(): boolean {
      return (parseInt(this.values.adult, 10) + parseInt(this.values.senior, 10)) > 0
    },
    showBottom(): boolean {
      return !getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.values = { ...defaultCustomerValues, ...value };
      },
    },
    'values.child': {
      handler(newValue, oldValue) {
        this.$emit('child-number-updated', newValue);
        if(this.isChildAgeConfigurationAvailable) {
          this.handleChildAgeSelectorCreation(newValue, oldValue);
        }
      },
      deep: true
    },
    hasAdult: {
      immediate: true,
      handler(newValue) {
        if (!newValue && this.values.child) {
          this.resetChildAgeGroupConfiguration()
        }
      },
    },
    closeSelectorTriggered: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.apply();
        }
      }
    }

  },
  created() {
    this.$emit('init');
  },
  mounted(){
    if (this.isChildAgeConfigurationAvailable) {
        this.createAgeRangeFromConfig();
        this.handleDefinedChildAgesSelectorCreation();
    }
  },
  methods: {
    apply() {
      if(this.isChildAgeConfigurationAvailable) {
        if (this.isCustomerChildSelectorsValid()) {
         this.$emit('input', this.getCustomerSelectedChildAgeRange());
        }
      }
      if(!this.isChildAgeConfigurationAvailable){
        this.$emit('input', { ...this.values });
      }
    },
    getCustomerSelectedChildAgeRange(): object {
      if(this.isChildAgeConfigurationAvailable) {
        return {
          adult: this.values.adult,
          child: this.values.child,
          senior: this.values.senior,
          ...(
            this.values.child > 0 ? { children_ages: this.customerChildAgeSelectorsList.map(({selectedValue}) => +selectedValue?.name) } : {}
          )
        }
      }

      return { ...this.values }
    },
    isCustomerChildSelectorsValid(): boolean {
      let hasInvalid = this.customerChildAgeSelectorsList.some(item => {
        if (!item.selectedValue || item.selectedValue?.id === '' || item.selectedValue?.name === '') {
            item.error = true;
            return true;
        } else {
            item.error = false;
            return false;
        }
      });
      return !hasInvalid;
    },
    onSelectChildAge(id:string):void{
      const childAgeSelector = this.customerChildAgeSelectorsList.find(item => item.id === id);
        if (childAgeSelector) {
          childAgeSelector.error = false;
          this.updateValues();
        }
    },
    handleChildAgeSelectorCreation(newChildCount: number, oldChildCount: number): void {
        if (newChildCount > oldChildCount) {
            const currentCount = this.customerChildAgeSelectorsList.length;
            const itemsToAdd = newChildCount - oldChildCount;

            const newItems = Array.from({ length: itemsToAdd }, (_, index) => ({
              isDropDownOpened: false,
              id: 'childAgeDropDown-' + (currentCount + index + 1),
              selectedValue: {id:'', name: ''},
              error: false
            }));
            this.customerChildAgeSelectorsList = this.customerChildAgeSelectorsList.concat(newItems);
          }
          if (newChildCount < oldChildCount) {
            this.customerChildAgeSelectorsList.splice(newChildCount);
          }
          if (newChildCount <= 0) {
            this.customerChildAgeSelectorsList = [];
          }
    },
    handleDefinedChildAgesSelectorCreation(): void{
      const { values } = this;
      if (values.child > 0 && values.children_ages.length > 0) {
        values.children_ages.forEach((age: number, index: number) => {
          this.customerChildAgeSelectorsList.push({
            isDropDownOpened: false,
            id: `childAgeDropDown-${index + 1}`,
            selectedValue:  { id: `${age}`, name: `${age}` },
            error: false
          });
        });
      }
    },
    filterListBasedOnConfig() {
      const { defaultList, config } = this;
        if (this.isChildAgeConfigurationAvailable) {
          // Include only 'adult' and 'child' when child_age_configuration is true
          const allowedCategories = ['adult', 'child'];
          // If 'senior' is in config, add this also
          if (config['senior']) {
            allowedCategories.push('senior');
          }
          return defaultList.filter(item => allowedCategories.includes(item.id));
        } else {
          // Include items based on the existence of the item's id as a key in config
          return defaultList.filter(item => config[item.id]);
        }
    },
    createAgeRangeFromConfig() {
      const { config } = this;
        if (!config || !config.adult || !config.adult.min_age) {
          console.error('Invalid configuration object');
          return;
        }

        const minAge = parseInt(config.adult.min_age, 10) - 1;
        this.customerChildAgeRangesList = Array.from({ length: minAge + 1 }, (_, index) => ({
            id: index,
            name: `${index}`,
        }));
    },
    updateValues() {
      if(this.isChildAgeConfigurationAvailable) {
        if(this.isCustomerChildSelectorsValid()) {
          this.$emit('update-customers', this.getCustomerSelectedChildAgeRange());
        }
      }
      if(!this.isChildAgeConfigurationAvailable){
        this.$emit('update-customers', { ...this.values });
      }
    },
    resetChildAgeGroupConfiguration(): void {
      this.values.child = 0;
      this.customerChildAgeSelectorsList = []
    },
    //custom emit to handle esc key to close dropdown and keep the focus on dropdown wrapper for accessibility
    handleEscKey(): void {
      this.$emit('esc-pressed');
    },

    closeDropdown() {
      this.$emit('close');
    }
  },
});
